import i18next from "i18next";
import { DateRange, DateTimeFormat, DurationFormat, I18nTools } from "../..";

const pad = (value: number) => (value < 10 ? "0" + value : String(value));

/**
 * Get the short text representation for a month.
 * @param value The month number, eg 1 for january
 */
const getMonthTextShort = (value: number) => {
  switch (value) {
    case 1:
      return i18next.t("imagine:januaryShort");
    case 2:
      return i18next.t("imagine:februaryShort");
    case 3:
      return i18next.t("imagine:marchShort");
    case 4:
      return i18next.t("imagine:aprilShort");
    case 5:
      return i18next.t("imagine:mayShort");
    case 6:
      return i18next.t("imagine:juneShort");
    case 7:
      return i18next.t("imagine:julyShort");
    case 8:
      return i18next.t("imagine:augustShort");
    case 9:
      return i18next.t("imagine:septemberShort");
    case 10:
      return i18next.t("imagine:octoberShort");
    case 11:
      return i18next.t("imagine:novemberShort");
    case 12:
      return i18next.t("imagine:decemberShort");
    default:
      throw new Error("Month out of range:" + value);
  }
};

/**
 * Get the full text representation of a month
 * @param value The month number, eg 1 for january
 */
const getMonthTextFull = (value: number) => {
  switch (value) {
    case 1:
      return i18next.t("imagine:january");
    case 2:
      return i18next.t("imagine:february");
    case 3:
      return i18next.t("imagine:march");
    case 4:
      return i18next.t("imagine:april");
    case 5:
      return i18next.t("imagine:may");
    case 6:
      return i18next.t("imagine:june");
    case 7:
      return i18next.t("imagine:july");
    case 8:
      return i18next.t("imagine:august");
    case 9:
      return i18next.t("imagine:september");
    case 10:
      return i18next.t("imagine:october");
    case 11:
      return i18next.t("imagine:november");
    case 12:
      return i18next.t("imagine:december");
    default:
      throw new Error("Month out of range:" + value);
  }
};

const getMonthTextFullLower = (month: number) => {
  const text = getMonthTextFull(month);
  return text.toLowerCase();
};

/**
 * Get a text representation of a list of months (full month names). The months are separated by a comma.
 * @param months
 * @returns
 */
const getMonthListText = (months: number[]) => {
  let monthStrings = "";
  months.forEach((month: number) => {
    if (monthStrings === "") monthStrings = getMonthTextFull(month);
    else monthStrings += ", " + getMonthTextFullLower(month);
  });
  if (monthStrings === "") return "";
  return monthStrings;
};

/**
 * Get a text representation of the months (full month names). If there is multiple months, only the first and last
 * month is included, separated by a dash.
 */
const getMonthListTextShort = (months: number[]) => {
  let monthStrings = "";
  months.forEach((month: number, index: number) => {
    if (monthStrings === "") {
      monthStrings = getMonthTextFull(month);
    } else {
      // Next month is also included
      if (
        months.length >= index + 1 &&
        months[index + 1] === month + 1 &&
        months[index - 1] === month - 1
      ) {
        if (monthStrings[monthStrings.length - 1] !== "-") monthStrings += "-";
      } else {
        if (monthStrings[monthStrings.length - 1] !== "-") {
          // Dash if previous month was right before this, and comma otherwise (month gap)
          if (months[index - 1] === month - 1) monthStrings += "-";
          else monthStrings += ", ";
        }
        monthStrings += getMonthTextFullLower(month);
      }
    }
  });
  if (monthStrings === "") return "";
  return monthStrings;
};

/**
 * Converts a date into a string with a given format. Default is db format with date and time.
 * The date is displayed in the browser's time zone and formatted according to the user's language.
 * @param date The date object (expects UTC time)
 */
const getDateTimeText = (
  date: Date,
  format: DateTimeFormat = DateTimeFormat.BACKEND_DATE_AND_TIME,
) => {
  try {
    let options: Intl.DateTimeFormatOptions;
    let formatter: Intl.DateTimeFormat;
    const locale = i18next.language
      ? I18nTools.getLanguageTag(i18next.language)
      : [];

    switch (format) {
      case DateTimeFormat.BACKEND_DATE: {
        const m = date.getUTCMonth() + 1.0;
        const d = date.getUTCDate();
        return (
          date.getUTCFullYear() +
          "-" +
          (m < 10 ? "0" : "") +
          m +
          "-" +
          (d < 10 ? "0" : "") +
          d
        );
      }
      case DateTimeFormat.BACKEND_TIME: {
        return (
          pad(date.getUTCHours()) +
          ":" +
          pad(date.getUTCMinutes()) +
          ":" +
          pad(date.getUTCSeconds())
        );
      }
      case DateTimeFormat.BACKEND_DATE_AND_TIME: {
        const m = date.getUTCMonth() + 1.0;
        const d = date.getUTCDate();
        return (
          date.getUTCFullYear() +
          "-" +
          (m < 10 ? "0" : "") +
          m +
          "-" +
          (d < 10 ? "0" : "") +
          d +
          " " +
          pad(date.getUTCHours()) +
          ":" +
          pad(date.getUTCMinutes()) +
          ":" +
          pad(date.getUTCSeconds())
        );
      }
      case DateTimeFormat.SHORT_DATE_NO_YEAR:
        return date.getDate() + "/" + (date.getMonth() + 1.0);
      case DateTimeFormat.LONG_DATE:
        options = { dateStyle: "long" };
        // Empty array as locale will select the browser locale
        formatter = new Intl.DateTimeFormat(locale, options);
        return formatter.format(date);
      case DateTimeFormat.SHORT_DATE:
        options = { dateStyle: "short" };
        // Empty array as locale will select the browser locale
        formatter = new Intl.DateTimeFormat(locale, options);
        return formatter.format(date);
      case DateTimeFormat.DATE_AND_TIME:
        options = { dateStyle: "long", timeStyle: "short" };
        // Empty array as locale will select the browser locale
        formatter = new Intl.DateTimeFormat(locale, options);
        return formatter.format(date);
      case DateTimeFormat.SHORT_DATE_AND_TIME:
        options = { dateStyle: "short", timeStyle: "short" };
        // Empty array as locale will select the browser locale
        formatter = new Intl.DateTimeFormat(locale, options);
        return formatter.format(date);
      case DateTimeFormat.LONG_DATE_AND_TIME:
        options = { dateStyle: "long", timeStyle: "short" };
        // Empty array as locale will select the browser locale
        formatter = new Intl.DateTimeFormat(locale, options);
        return formatter.format(date);
      case DateTimeFormat.SHORT_TIME:
        options = { timeStyle: "short" };
        // Empty array as locale will select the browser locale
        formatter = new Intl.DateTimeFormat(locale, options);
        return formatter.format(date);
      case DateTimeFormat.LONG_TIME:
        options = { timeStyle: "long" };
        // Empty array as locale will select the browser locale
        formatter = new Intl.DateTimeFormat(locale, options);
        return formatter.format(date);
      default:
        throw new Error(
          "Missing implementation for date time display: " + format,
        );
    }
  } catch (e) {
    console.error(e);
    return "";
  }
};

const getDateTimeTextNowForDb = () => {
  const date = new Date();
  return getDateTimeText(date, DateTimeFormat.BACKEND_DATE_AND_TIME);
};

/**
 * Returns a pretty formatted date.
 * The date is displayed in the browser's time zone and formatted according to the user's language.
 * @param date The date in DB format
 * @param format The format to use on return date.
 */
const getDbDateTimeText = (date: string, format: DateTimeFormat) => {
  if (date === "") return "";
  // All dates from backend are in UTC
  if (!date.includes("Z") && !date.includes("UTC")) {
    date += "Z";
  }
  try {
    const dateType = new Date(date);
    return getDateTimeText(dateType, format);
  } catch (e) {
    console.error(e);
    console.error("Error parsing date:", date);
    return "";
  }
};

const getTimestampDateTimeText = (
  timestamp: number,
  format: DateTimeFormat,
) => {
  const date = new Date(timestamp * 1000);
  return getDateTimeText(date, format);
};

/**
 * Get a date time text for a future date.
 * @param days Days to add to the current date
 * @param format
 * @returns
 */
const getFutureDateTimeText = (days: number, format: DateTimeFormat) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return getDateTimeText(date, format);
};

const getDateRangeText = (range: DateRange) => {
  switch (range) {
    case DateRange.CUSTOM:
      return i18next.t("imagine:custom");
    case DateRange.LAST_7_DAYS:
      return i18next.t("imagine:last7Days");
    case DateRange.LAST_30_DAYS:
      return i18next.t("imagine:last30Days");
    case DateRange.LAST_3_MONTHS:
      return i18next.t("imagine:last3Months");
    case DateRange.LAST_6_MONTHS:
      return i18next.t("imagine:last6Months");
    case DateRange.LAST_YEAR:
      return i18next.t("imagine:lastYear");
    case DateRange.NONE:
      return i18next.t("imagine:none");
    default:
      throw new Error("Unknown date range:" + range);
  }
};

/**
 * Returns a pretty formatted duration.
 * @param duration The duration in seconds
 * @param format The format to use on return duration.
 */
const getDurationText = (duration: number, format: DurationFormat) => {
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = Math.floor(duration % 60);
  let text = "";
  if (format === DurationFormat.SHORT) {
    if (hours > 0) text += hours + ":";
    if (minutes > 0 || hours > 0) text += minutes + ":";
    if (seconds > 0 || minutes > 0 || hours > 0) text += seconds;
    return text;
  }
  if (format === DurationFormat.LONG) {
    if (hours > 0) text += i18next.t("imagine:hours", { count: hours }) + " ";
    if (minutes > 0)
      text += i18next.t("imagine:minutes", { count: minutes }) + " ";
    if (seconds > 0) text += i18next.t("imagine:seconds", { count: seconds });
    return text;
  }
  return "";
};

export const DateTimeText = {
  getMonthTextShort,
  getMonthTextFull,
  getMonthTextFullLower,
  getMonthListText,
  getMonthListTextShort,
  getDateTimeText,
  getDateTimeTextNowForDb,
  getDbDateTimeText,
  getTimestampDateTimeText,
  getFutureDateTimeText,
  getDateRangeText,
  getDurationText,
};
